import React from 'react'
import { Icon } from '@chakra-ui/icons'
import { Link, Text } from '@chakra-ui/react'
import { Tooltip } from '@chakra-ui/react'
import { Menu, MenuButton, MenuList, Portal } from '@chakra-ui/react'
import { FiCopy, FiExternalLink, FiShield } from 'react-icons/fi'
import { Button, IconButton, ButtonProps } from '@chakra-ui/react'

import { useUserContext } from '~config/user-context'
import MenuItem from '~components/shared/menu/menu-item'
import { getSupabaseLink, SupabaseEntities } from '~config/constants'
import ActionButton from './action-button'

interface Props {
    entity: SupabaseEntities
    id: string
    pms_id?: string | null | undefined
    extraCopyOptions?: AdminCopyOption[]
    type?: 'icon' | 'text'
    buttonProps?: ButtonProps
}

interface AdminCopyOption {
    label: string
    value?: string | null | undefined
}

export default function AdminIdButton({
    entity,
    id,
    pms_id,
    extraCopyOptions = [],
    type = 'text',
    buttonProps,
}: Props) {
    const { isAdmin } = useUserContext()

    if (!isAdmin) return <></>

    return (
        <Menu placement="bottom-end">
            <ButtonComponent type={type} buttonProps={buttonProps} />
            <Portal>
                <MenuList>
                    <MenuItem
                        icon={FiCopy}
                        onClick={e => {
                            e.stopPropagation()
                            navigator.clipboard.writeText(id)
                        }}
                    >
                        Copy {entity} ID
                    </MenuItem>
                    {pms_id && (
                        <MenuItem
                            icon={FiExternalLink}
                            onClick={() => {
                                navigator.clipboard.writeText(pms_id)
                            }}
                        >
                            Copy PMS ID
                        </MenuItem>
                    )}
                    {extraCopyOptions
                        .filter(({ value }) => Boolean(value))
                        .map(({ label, value }) => (
                            <MenuItem
                                key={label}
                                icon={FiCopy}
                                onClick={e => {
                                    e.stopPropagation()
                                    navigator.clipboard.writeText(value || '')
                                }}
                            >
                                Copy {label}
                            </MenuItem>
                        ))}
                    <Link
                        variant="unstyled"
                        href={getSupabaseLink(entity, id)}
                        isExternal
                    >
                        <MenuItem icon={FiExternalLink}>
                            View in Supabase
                        </MenuItem>
                    </Link>
                </MenuList>
            </Portal>
        </Menu>
    )
}

function ButtonComponent({
    type,
    buttonProps,
}: Pick<Props, 'type' | 'buttonProps'>) {
    const sharedProps: ButtonProps = {
        size: 'sm',
        bg: 'white',
        shadow: 'sm',
        variant: 'outline',
        borderColor: 'blackAlpha.300',
        ...buttonProps,
    }

    if (type === 'icon') {
        return (
            <MenuButton
                as={ActionButton}
                icon={<AdminIcon />}
                tooltipLabel="Admin Options"
                onClick={e => e.stopPropagation()}
                {...sharedProps}
            />
        )
    }

    return (
        <MenuButton
            as={Button}
            leftIcon={<Text>🤫</Text>}
            onClick={e => e.stopPropagation()}
            {...sharedProps}
        >
            Admin Options
        </MenuButton>
    )
}

function AdminIcon() {
    return <Icon as={FiShield} w="16px" h="16px" />
}
